import { HOME_COLLAGE } from "@/content/home-collage";
import { useT } from "@/lib/i18n-dict";

const panel =
  "overflow-hidden rounded-lg border border-border bg-card shadow-iso-flat transition-transform duration-500 ease-out hover:z-30 hover:rotate-0 hover:-translate-y-1 motion-reduce:transform-none motion-reduce:transition-none";

function PanelHeader({ title, code }: { title: string; code: string }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/70 bg-surface px-3 py-2">
      <span className="truncate font-display text-xs font-semibold text-foreground">{t(title)}</span>
      <span className="shrink-0 font-mono text-[0.55rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
        {code}
      </span>
    </div>
  );
}

function SchedulePanel() {
  const t = useT();
  const { schedule } = HOME_COLLAGE;
  return (
    <div className={`${panel} rotate-[1.5deg] sm:absolute sm:top-0 sm:right-0 sm:w-[72%] sm:rotate-[4deg]`}>
      <PanelHeader title={schedule.title} code="APS-01" />
      <div className="p-3">
        <div className="mb-2 grid grid-cols-[4.5rem_repeat(4,1fr)] gap-1 font-mono text-[0.48rem] text-muted-foreground">
          <span />
          {schedule.times.map((time) => <span key={time} className="text-center">{time}</span>)}
        </div>
        <div className="space-y-2">
          {schedule.lines.map((line, index) => (
            <div key={line} className="grid grid-cols-[4.5rem_1fr] items-center gap-2">
              <span className="truncate text-[0.58rem] font-medium text-muted-foreground">{t(line)}</span>
              <span className="relative h-5 overflow-hidden rounded-sm bg-muted">
                <span className="absolute inset-y-1 rounded-sm border border-primary/50 bg-primary/25" style={{ insetInlineStart: `${index * 14}%`, width: `${55 - index * 5}%` }} />
                <span className="absolute inset-y-1 rounded-sm border border-foreground/15 bg-foreground/10" style={{ insetInlineStart: `${62 - index * 8}%`, width: "26%" }} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrdersPanel() {
  const t = useT();
  const { orders } = HOME_COLLAGE;
  return (
    <div className={`${panel} -rotate-[1deg] sm:absolute sm:top-24 sm:left-0 sm:z-10 sm:w-[56%] sm:-rotate-[7deg]`}>
      <PanelHeader title={orders.title} code="MO-LIVE" />
      <div className="divide-y divide-border/60 px-3">
        {orders.rows.map((row) => (
          <div key={row.order} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 py-2.5">
            <span className="row-span-2 grid size-8 place-items-center rounded-sm bg-primary text-[0.58rem] font-bold text-primary-foreground">{row.order.slice(-3)}</span>
            <span className="flex min-w-0 items-center justify-between gap-2 text-[0.58rem]">
              <span className="truncate text-muted-foreground">{t(row.label)}</span>
              <strong className="shrink-0 text-foreground">{row.value}</strong>
            </span>
            <span className="h-1.5 overflow-hidden rounded-full bg-muted">
              <span className="block h-full rounded-full bg-primary" style={{ width: `${row.progress}%` }} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CentresPanel() {
  const t = useT();
  const { centres } = HOME_COLLAGE;
  return (
    <div className={`${panel} rotate-[1deg] sm:absolute sm:right-3 sm:bottom-4 sm:w-[59%] sm:rotate-[3deg]`}>
      <PanelHeader title={centres.title} code="WC-LOAD" />
      <div className="space-y-3 p-3">
        {centres.rows.map((row) => (
          <div key={row.label} className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-2">
            <span className="truncate text-[0.58rem] text-muted-foreground">{t(row.label)}</span>
            <span className="h-2 overflow-hidden rounded-full bg-muted"><span className="block h-full bg-primary" style={{ width: `${row.value}%` }} /></span>
            <strong className="font-mono text-[0.55rem] text-foreground">{row.value}%</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function UtilizationPanel() {
  const t = useT();
  const { utilization } = HOME_COLLAGE;
  return (
    <div className={`${panel} -rotate-[1deg] sm:absolute sm:bottom-0 sm:left-5 sm:z-20 sm:w-[36%] sm:-rotate-[4deg]`}>
      <div className="flex items-center gap-3 p-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-full border-4 border-primary/30 bg-primary/10 font-mono text-xs font-bold text-foreground">{utilization.value}</span>
        <span className="min-w-0">
          <span className="block truncate text-[0.62rem] font-semibold text-foreground">{t(utilization.title)}</span>
          <span className="block text-[0.55rem] text-muted-foreground">{t(utilization.label)}</span>
        </span>
      </div>
    </div>
  );
}

export function HomeProductCollage() {
  const t = useT();
  return (
    <div className="relative mx-auto -mt-6 w-full max-w-[500px] sm:h-[420px]" role="img" aria-label={t("Live production planning and shop-floor control")}> 
      <div className="grid gap-3 sm:block">
        <SchedulePanel />
        <OrdersPanel />
        <CentresPanel />
        <UtilizationPanel />
      </div>
    </div>
  );
}